import { asStringArray, asStringMap, mapDtoList, requireObject, requireString } from './parse.js';
import { UserStatus } from './UserStatus.js';

/**
 * Mirrors com.medprompt.identity.user.UserData (API responses, never storage secrets).
 *
 * @typedef {object} UserData
 * @property {string} userId
 * @property {string} email
 * @property {string} name
 * @property {string[]} clinicIds
 * @property {string} role
 * @property {string} userStatus PENDING | ACTIVE | BLOCKED
 * @property {string[]} teamIds
 * @property {Object<string, string>} settings
 * @property {{ returnsAt: number|string|null }|null} outOfOffice null when in office; returnsAt null means until cleared
 * @property {string|null} nhsUserId
 * @property {number|string|null} lastSignedIn Jackson Instant (epoch millis or ISO-8601)
 */

function parseOutOfOffice(value) {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const returnsAt = value.returnsAt ?? null;
  if (returnsAt != null && typeof returnsAt !== 'string' && typeof returnsAt !== 'number') {
    return null;
  }
  return { returnsAt };
}

/**
 * @param {object} raw
 * @returns {UserData}
 */
export function fromUserData(raw) {
  const source = requireObject(raw, 'UserData');
  const userStatus = requireString(source, 'userStatus', 'UserData');
  if (!Object.values(UserStatus).includes(userStatus)) {
    throw new Error(`UserData: unknown userStatus "${userStatus}"`);
  }

  return {
    userId: requireString(source, 'userId', 'UserData'),
    email: requireString(source, 'email', 'UserData'),
    name: requireString(source, 'name', 'UserData'),
    clinicIds: asStringArray(source.clinicIds),
    role: requireString(source, 'role', 'UserData'),
    userStatus,
    teamIds: asStringArray(source.teamIds),
    settings: asStringMap(source.settings),
    outOfOffice: parseOutOfOffice(source.outOfOffice),
    nhsUserId: typeof source.nhsUserId === 'string' ? source.nhsUserId : null,
    lastSignedIn: source.lastSignedIn ?? null,
  };
}

/**
 * @param {unknown} raw
 * @returns {UserData[]}
 */
export function fromUserDataList(raw) {
  return mapDtoList(raw, 'UserData', fromUserData);
}

/**
 * Mirrors com.medprompt.identity.user.UserUpdateRequest
 *
 * @param {{ name?: string, role?: string, addedClinicIds?: string[], removedClinicIds?: string[], markOutOfOffice?: boolean, returnsAt?: string|null }} fields
 * @returns {object}
 */
export function toUserUpdateRequest(fields) {
  const body = {};
  if (fields.name !== undefined) {
    body.name = fields.name;
  }
  if (fields.role !== undefined) {
    body.role = fields.role;
  }
  if (fields.addedClinicIds !== undefined) {
    body.addedClinicIds = fields.addedClinicIds;
  }
  if (fields.removedClinicIds !== undefined) {
    body.removedClinicIds = fields.removedClinicIds;
  }
  if (fields.markOutOfOffice !== undefined) {
    body.markOutOfOffice = fields.markOutOfOffice;
  }
  if (fields.returnsAt !== undefined) {
    body.returnsAt = fields.returnsAt;
  }
  return body;
}

/**
 * Mirrors com.medprompt.identity.user.UserOnboardRequest.
 * Java serializes clinicIds as clinic_ids.
 *
 * @param {{ email: string, name: string, role: string, clinicIds: string[] }} fields
 * @returns {{ email: string, name: string, role: string, clinic_ids: string[] }}
 */
export function toUserOnboardRequest({ email, name, role, clinicIds }) {
  if (typeof email !== 'string' || !email) {
    throw new Error('UserOnboardRequest: email is required');
  }
  if (typeof name !== 'string' || !name) {
    throw new Error('UserOnboardRequest: name is required');
  }
  if (typeof role !== 'string' || !role) {
    throw new Error('UserOnboardRequest: role is required');
  }
  return {
    email,
    name,
    role,
    clinic_ids: asStringArray(clinicIds),
  };
}
